import React, { Fragment, useRef } from "react";
import { useEffectOnce } from "../../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";
import { 
    setProcessing, setPageValue, loadPage, setPanes, loadDataset, createEntity, setFormServerErrors, 
    loadEntity, removeEntityFromUI, saveEntity, deleteEntity, setFormValue, entityCloned,
    openFieldsDialog, closeFieldsDialog, setFieldChecked, clearFields, setFieldValue, 
    openComputedFieldDialog, closeComputedFieldDialog, openExpressionFieldDialog, closeExpressionFieldDialog,
    disposePage
} from "../../redux/slices/datasourcespageslice";
import { addNotification } from "../../redux/slices/notificationsslice";
import { setCurentPage, setCurrentPageIsDirty, setCurrentPage } from "../../redux/slices/currentpageslice";
import { cloneDataSource, initEntityCloner } from "../../redux/slices/entityclonerslice";
import { notificationDurationMiddle } from "../../lib/utils";
import { hideAppDialog, setAppDialog } from "../../redux/slices/appdialogslice";
import { useNavigate, useLocation } from "react-router-dom";
import { apiErrorsHandler, getErrorMessages } from "../../api/httpclient";
import { Page } from "../page";
import { EntityCloner } from "../../components/entitycloner";
import { DataSourcesPageBody } from "./datasourcespagebody";
import { formValidator } from "./datasourcespageformvalidator";
import { isEmptyObject, getExpressionBodyTokens, deepCompare } from "../../lib/utils";
import { ComputedFieldFunction } from "../../lib/computedfieldfunction";
import { BLOCK_DIRTY_MESSAGE } from "../../settings";

import "./datasourcespage.css"; 
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { dataSourceMaxReached } from "../../routing/authorizationhandlers";

export const DataSourcesPage = props => {
    const dispatch = useDispatch();
    const currentUser = useSelector(state => state.currentUser);
    const currentTheme = useSelector(state => state.currentTheme);
    const pageState = useSelector(state => state.dataSourcesPage);
    const navigate = useNavigate();
    const currentPageState = useSelector(state => state.currentPage);
    const { t } = useTranslation();

    let currentPageId = "dataSourcesPage";

    /*block navigation if dirty*/
    const updateDirtyState = () => {
        let isDirty = !deepCompare(pageState.formInitialValues, pageState.formValues);
        let wasDirty = currentPageState.isDirty;

        if(isDirty != wasDirty)
            dispatch(setCurrentPageIsDirty(isDirty));
    };

    const blockNavigationIfDirty = (msg) => {
        if (currentPageState.isDirty)
        {
            if (window.confirm(msg ? msg : BLOCK_DIRTY_MESSAGE) == true)
                {
                    dispatch(setCurrentPageIsDirty(false));
                    return false;
                }
            else
                return true;
        }
        else
            return false;
    };
    /*block navigation if dirty*/


    /*initial page load*/
    useEffectOnce(() => {
        dispatch(loadPage(t)).then(
            response => {
                dispatch(setCurrentPage({id: currentPageId, isDirty: false}));
            },
            err =>{
                //hide loader
                dispatch(setProcessing({processing: false}));

                const handleResponseReceived = (state, dispatch, navigate, payload) =>{
                    let errorMessages = payload?.data?.ErrorMessages;

                    //notify with error
                    if (!errorMessages || errorMessages.length == 0)
                        dispatch(addNotification({text: t("datasources.loading_errormessage"), type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                    else
                    {
                        errorMessages = getErrorMessages(errorMessages, 1);
                        dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                    }

                    //go home
                    navigate("/", {replace: true});
                };

                const handleUnhandled = (state, dispatch, navigate, payload) =>{
                    dispatch(addNotification({text: t("datasources.errormessage"), type:"error", closable: true, duration: notificationDurationMiddle, position: "topRight"}));
                    navigate("/", {replace: true});
                };

                apiErrorsHandler({}, dispatch, navigate, err, "/", {responseReceived: handleResponseReceived, unhandled: handleUnhandled});
            }
        );

        return () => {
            dispatch(setCurrentPage({id: "", isDirty: false}));
            dispatch(disposePage(true));
        }
    }, []);
    /*initial load*/


    /*splitter*/
    const handleOnSplitterChange = e => {
        dispatch(setPanes(e.newState));
    };
    /*splitter*/

    /*panel bar*/
    const dataSetsPanelRef = useRef(null);

    //on data set change
    const handleOnDatasetChange = e => {
        dispatch(loadDataset(e.value, t)).then(
            response =>{
                if (blockNavigationIfDirty() == true)
                    return;
            },
            err => {
                //hide loader
                dispatch(setProcessing({processing: false}));

                const handleResponseReceived = (state, dispatch, navigate, payload) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                        errorMessages = [t("datasources.errormessage")];
                    else                    
                        errorMessages = getErrorMessages(errorMessages, 1);                                         

                    dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                };

                const handleUnhandled = (state, dispatch, navigate, payload) =>{
                    let errorMessage = t("datasources.errormessage");
                    dispatch(addNotification({text: errorMessage, type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                };

                apiErrorsHandler({}, dispatch, navigate, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});                
            }
        );       
    };  

    /*panel bar*/


    /*toolbar*/
    const handleOnNew = e =>{
        if (!currentUser.IsAuthenticated || dataSourceMaxReached(currentUser))
        {
                dispatch(addNotification({text: t("datasources.errormessage1"), type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                return;
        }

        if (blockNavigationIfDirty() == true)
            return;

        dispatch(createEntity());
    };

    const handleOnSave = e =>{
        if (pageState.mode == 1)
        {
            //create
            if (!currentUser.IsAuthenticated || dataSourceMaxReached(currentUser))
            {
                dispatch(addNotification({text: t("datasources.errormessage1"), type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                return;
            }
        }
        
        //make sure no client errors; if so return
        let formErrors = formValidator(pageState.formValues);
        if (formErrors !== undefined)
        {            
            dispatch(addNotification({text: t("datasources.errormessage2"), type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
            return;
        }

        //no client errors => proceed        
        dispatch(saveEntity(t)).then(
            response =>{
                dispatch(setCurrentPageIsDirty(false));
            },
            err =>
            {
                //hide loader
                dispatch(setProcessing({processing: false}));

                const handleResponseReceived = (state, dispatch, navigate, payload) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                        errorMessages = [t("datasources.errormessage")];
                    else
                    {
                        errorMessages = getErrorMessages(errorMessages, 1);

                        //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
                        if (payload.data.ErrorCodes.EntityDoesNotExist)
                        {
                            dispatch(removeEntityFromUI(pageState.formValues.Id));
                            dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
                        }
                        else
                            dispatch(setFormServerErrors(errorMessages));
                    }
                };

                const handleUnhandled = (state, dispatch, navigate, payload) =>{
                    let errorMessage = t("datasources.errormessage");
                    dispatch(setFormServerErrors([errorMessage]));                
                };

                apiErrorsHandler({}, dispatch, navigate, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});                
            }
        );
    };

    const handleOnClone = e => {
        dispatch(initEntityCloner({title: pageState.formValues.Title, navigate: true}));

        dispatch(setAppDialog({
            isVisible: true, closable: true, title: t("datasources.title"), className: "",
            content: <EntityCloner className=""/>,
            actions: {
              yesButton: {
                title: t("datasources.subtitle"), handler: e => {
                    dispatch(cloneDataSource(t)).then(
                        response => {
                            dispatch(hideAppDialog());

                            dispatch(addNotification({text: t("datasources.successmessage"), type: "success", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

                            
                            dispatch(entityCloned({id: response.id, title: response.title, navigate: response.navigate}));
                        },
                        err => {
                            if (err == "invalidData")
                            {
                                //show notification that title is required
                                dispatch(addNotification({text: t("datasources.invaliddata"), type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
                                return;
                            }
                            else if (err == "ignore")
                            {
                                //in this case the user clicked clone while it's cloning
                                //just do nothing
                                return;
                            }
                            else
                            {
                                const handleResponseReceived = (state, dispatch, navigate, payload) => {
                                    let errorMessages = payload?.data?.ErrorMessages;
                                    let errorMessage = t("datasources.errormessage");

                                    if (errorMessages || errorMessages.length > 0)
                                    {
                                        errorMessages = getErrorMessages(errorMessages, 1);
                
                                        //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
                                        if (payload.data.ErrorCodes.TitleExists)
                                            errorMessage = errorMessages[0];
                                    }

                                    dispatch(addNotification({text: errorMessage, type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
                                };
                
                                const handleUnhandled = (state, dispatch, navigate, payload) =>{

                                    dispatch(addNotification({text: t("datasources.errormessage"), type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
                                };                            
                
                                apiErrorsHandler({}, dispatch, null, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});    

                                return Promise.resolve();
                            }
                        }
                    )
                }
              },
              noButton: {
                title: t("datasources.cancel")
              }
            }
          }));
    };

    const handleOnErrorbarClose = e => {
        dispatch(setFormServerErrors([]));
    };
    /*toolbar*/


    /*delete entity*/
    const handleOnDelete = (id) => {        
        //id is zero if deleted from toolbar and > 0 if deleted from entities list
        //if id is zero and mode !=2 => miust not happen
        if (!id && pageState.mode != 2)
            return;        

        if (!id)//deleted from the toolbar => it's the current entity and mode is 2
          id = pageState.formValues.Id;

          let isCurrentEntity = false;

          if (!id)//deleted from the toolbar => it's the current entity and mode is 2
          {
              id = pageState.formValues.Id;
              isCurrentEntity = true;
          }
          else if (pageState.formValues?.Id && pageState.formValues.Id == id)
              isCurrentEntity = true;
  
          if (isCurrentEntity)
          {
              //if contains dirty => block
              if (blockNavigationIfDirty() == true)
                  return;
          }

        dispatch(setAppDialog({
            isVisible: true,
            className: "datasources-page-are-you-sure",
            title: t("datasources.warning"), content: t("datasources.delete"),
            actions:{
                yesButton:{ title: t("datasources.yes"), handler: () =>{
                    dispatch(hideAppDialog());

                    dispatch(deleteEntity(id, t))
                    .then(
                        response =>{
                        },
                        err => {
                            dispatch(setProcessing({processing: false}));

                            const handleResponseReceived = (state, dispatch, navigate, payload) => {
                                let errorMessages = payload?.data?.ErrorMessages;
            
                                if (!errorMessages || errorMessages.length == 0)
                                    errorMessages = t("datasources.errormessage");
                                else
                                {
                                    errorMessages = getErrorMessages(errorMessages, 1);
            
                                    if (payload.data.ErrorCodes.EntityDoesNotExist)
                                        dispatch(removeEntityFromUI(id));                        
                                }
            
                                dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
                            };
            
                            const handleUnhandled = (state, dispatch, navigate, payload) =>{
                                let errorMessage = t("datasources.errormessage");
                                dispatch(addNotification({text: errorMessage, type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
                            };
            
                            apiErrorsHandler({}, dispatch, navigate, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});                
                        });
                }},
                noButton: {title: t("datasources.no")}
            }
        }));
    };
    /*delete entity*/


    /*view/edit entity*/
    const handleOnEntitySelected = (entityId, item) => {
        if (blockNavigationIfDirty() == true)
            return;

        dispatch(loadEntity(entityId, t))
        .then(
            response =>{},
            err =>
            {
                //hid eloader
                dispatch(setProcessing({processing: false}));

                const handleResponseReceived = (state, dispatch, navigate, payload) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                        errorMessages = t("datasources.errormessage");
                    else
                    {
                        errorMessages = getErrorMessages(errorMessages, 1);

                        if (payload.data.ErrorCodes.EntityDoesNotExist)
                            dispatch(removeEntityFromUI(entityId));                        
                    }

                    dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                };

                const handleUnhandled = (state, dispatch, navigate, payload) =>{
                    let errorMessage = t("datasources.errormessage");
                    dispatch(addNotification({text: errorMessage, type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                };

                apiErrorsHandler({}, dispatch, navigate, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});                
            }
        );
    };
    /*view/edit entity*/


    /*TableValueList*/
    const handleOnOpenFieldsDialog = () => 
    {
        dispatch(openFieldsDialog());
    };

    const handleOnFieldsClear = (e, formRenderProps) => {
        if (pageState.mode == 2 && pageState.formValues.IsWidgetAttached)
            return;

        dispatch(clearFields());
        formRenderProps.onChange("Fields", {value: []});
    };


    /*remove from list of fields if type is table*/
    const handleOnFieldRemove = (item, t, formRenderProps) =>{       
        //get the field
        let toDeleteField = pageState.formValues.Fields.find(e => e.Id == item.Id);
        
        //prevent if a non-computed field refers to it
        let computedFields = pageState.formValues.Fields.filter(e => e.IsComputedField == true);
        if (computedFields?.length > 0)
        {
            for(let f in computedFields)
            {
                let field = computedFields[f];

                if(field.HasComputedFunction)
                {
                    if (field.ParentFieldId == toDeleteField.Id)
                    {
                        dispatch(addNotification({text: t("datasources.field") + field.Title + t("datasources.field2"), type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                        return;
                    }
                }
                else
                {
                    let res = getExpressionBodyTokens(field.ExpressionBody);
                    if (res.tokens?.length > 0)
                    {
                        for (let tk in res.tokens)
                        {
                            if (res.tokens[tk].trim().toLowerCase() == toDeleteField.Title.trim().toLowerCase())
                            {
                                dispatch(addNotification({text: t("datasources.field3") + field.Title + t("datasources.field2"), type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                                return;
                            }
                        }
                    }
                }
            }
        }
        
        let newFields = pageState.formValues.Fields.filter(e => e.Id != item.Id);

        dispatch(setFormValue({id: "Fields", value: newFields}));

        formRenderProps.onChange("Fields", {value:newFields});
    };

    const handleOnCloseFieldsDialog = () => dispatch(closeFieldsDialog());

    const handleOnFieldCheck = item =>{
        //we use FieldId because in the dialog, FieldId is unique
        //update fieldsToAdd.Checked of the clicked item
        dispatch(setFieldChecked(item.FieldId));
    };

    const getFieldMinIdAndMaxOrder = () => {
        let fields = [...pageState.formValues.Fields];

        let minId = 0;
        let maxOrderId = -1;//order is 0-based

        if (fields.length > 0)
        {
            for(let f in fields)
            {
                if (fields[f].Id < minId)
                    minId = fields[f].Id;

                if (fields[f].OrderId > maxOrderId)
                    maxOrderId = fields[f].OrderId;
            }
        }

        minId -= 1;
        maxOrderId += 1;

        return {id: minId, orderId: maxOrderId};
    };

    const handleOnSaveFieldsDialog = (formRenderProps) => {
        let fieldsToAdd = pageState.fieldsToAdd;//they are sorted by title
        let fields = [...pageState.formValues.Fields];

        if(!fieldsToAdd || fieldsToAdd.filter(e => e.Checked==true).length == 0)           
        {
            //nothing was selected
            dispatch(setPageValue({id:"fieldsDialogOpen", value: false}));
            dispatch(setPageValue({id:"fieldsToAdd", value: []}));
            return;
        }

        //clone fieldsToAdd and no need to sort it by title because it is already sorted
        fieldsToAdd = [...fieldsToAdd];

        //get minId from fields & maxOrderId
        let minId = 0;
        let maxOrderId = -1;//order is 0-based

        if (fields.length > 0)
        {
            for(let f in fields)
            {
                if (fields[f].Id < minId)
                    minId = fields[f].Id;

                if (fields[f].OrderId > maxOrderId)
                    maxOrderId = fields[f].OrderId;
            }
        }

        minId -= 1;
        maxOrderId += 1;

        for(let i in fieldsToAdd)
        {
            if (fieldsToAdd[i].Checked)
                fields.push({...fieldsToAdd[i], Id: minId--, SortDirection: 0, OrderId: maxOrderId++});
        }

        dispatch(setFormValue({id: "Fields", value: fields}));
        dispatch(setPageValue({id: "fieldsDialogOpen", value: false}));
        dispatch(setPageValue({id: "fieldsToAdd", value: []}));

        formRenderProps.onChange("Fields", {value: fields});
    };


    /*D&D*/
    const handleOnFieldDrop = (e, data, formRenderProps) =>{
        if (!data)
           return;

        let sourceItem = data.sourceItem;
        let targetItemId = data.targetItemId;

        if (sourceItem.Id == targetItemId)
            return;

        let sourceIndex = pageState.formValues.Fields.findIndex(e => e.Id == sourceItem.Id);
        let targetIndex = pageState.formValues.Fields.findIndex(e => e.Id == targetItemId);
        
        let newFields = [...pageState.formValues.Fields];
        let item = newFields[sourceIndex];

        //remove then insert then reorder
        newFields.splice(sourceIndex, 1);
        newFields.splice(targetIndex, 0, {...item});
        newFields = newFields.map((item, index) => ({...item, OrderId: index}));

        dispatch(setFormValue({id: "Fields", value: newFields}));

        formRenderProps.onChange("Fields", {value: newFields});
    };

    const handleOnFieldTitleChange = (e, item, formRenderProps) => {
        let value = e.value;

        //locate the field
        let field = pageState.formValues.Fields.find(e => e.Id == item.Id);

        dispatch(setFieldValue({...field, Title: value}));

        let newFields = [...pageState.formValues.Fields.filter(e => e.Id != field.Id), {...field, Title: value}];
        formRenderProps.onChange("Fields", {value: newFields});    
    };

    const handleonFieldGroupingChange = (e, item, formRenderProps) => {
        let value = e.value;

        //locate the field
        let field = pageState.formValues.Fields.find(e => e.Id == item.Id);
        let title = field.Title;
        let isGrouped = (value == 1000) ? true : false;

        let newField = {...field, FunctionId: value, Title: title, IsGrouped: isGrouped};
        let newFields = [...pageState.formValues.Fields.filter(e => e.Id != item.Id), {...newField}];

        dispatch(setFieldValue(newField));

        formRenderProps.onChange("Fields", {value: newFields});
    };
    
    const handleOnFieldSortDirectionChange = (e, item, formRenderProps) => {
        let value = e.value;

        //locate the field
        let field = pageState.formValues.Fields.find(e => e.Id == item.Id);

        let newField = {...field, SortDirection: value};

        let newFields = [...pageState.formValues.Fields.filter(e => e.Id != item.Id), {...newField}];

        dispatch(setFieldValue(newField));

        formRenderProps.onChange("Fields", {value: newFields});
    };
    
    const handleOnOpenComputedFieldDialog = (mode, fieldId, functionId, parentFieldId) => 
    {
        dispatch(openComputedFieldDialog({ mode: mode, fieldId: fieldId, functionId: functionId, parentFieldId: parentFieldId }));
    };

    const handleOnSaveComputedFieldDialog = (formRenderProps) => {
        if (handleOnValidateComputedFieldDialog() !== undefined)                
            return;

        let computedFunction = ComputedFieldFunction.getById(pageState.computedFieldFunctionId);
        let parentField = pageState.formValues.Fields.find(e => e.Id == pageState.computedFieldParentFieldId);
        let field = pageState.computedFieldMode == 1 ? null : pageState.formValues.Fields.find(e => e.Id == pageState.computedFieldFieldId);

        //insert into fields
        if (field == null)
        {
            let entry = getFieldMinIdAndMaxOrder();

            let newField = {
                Id: entry.id, 
                Name: (computedFunction.title + " of " + parentField.Title).substring(0, 50), 
                Title: (computedFunction.title + " of " + parentField.Title).substring(0, 50), 
                IsComputedField: true, 
                FunctionId: computedFunction.id, 
                FieldId: null,
                IsGrouped: false,
                ParentFieldId: parentField.Id,
                ExpressionBody: "",
                DataTypeId: computedFunction.getReturnTypeFromField(parentField.DataTypeId),
                IsDecimal: computedFunction.returnsDecimalFromFieldType(parentField.DataTypeId),
                IsPercent: computedFunction.returnsPercentageFromFieldType(parentField.DataTypeId),
                HasAggregateFunction: false, HasComputedFunction: true, HasExpressionBody: false,
                FieldIsAggregate: false,//field.FieldIsAggregate
                FieldIsExpression: false,//field.FieldIsAggregate
                OrderId: entry.orderId
            };

            let newFields = [...pageState.formValues.Fields, newField]

            dispatch(setFormValue({id: "Fields", value: newFields}));
            formRenderProps.onChange("Fields", { value: newFields});  
        }
        else
        {
            let newFields = pageState.formValues.Fields.map(e => {
                if (e.Id == field.Id)
                    return {...e, 
                        FunctionId: computedFunction.id, 
                        ParentFieldId: parentField.Id, 
                        DataTypeId: computedFunction.getReturnTypeFromField(parentField.DataTypeId),
                        IsDecimal: computedFunction.returnsDecimalFromFieldType(parentField.DataTypeId),
                        IsPercent: computedFunction.returnsPercentageFromFieldType(parentField.DataTypeId)
                    };
                else
                    return e;
            });

            dispatch(setFormValue({id: "Fields", value: newFields}));
            formRenderProps.onChange("Fields", { value: newFields});  
        }

        dispatch(setPageValue({id: "computedFieldDialogOpen", value: false}));
        dispatch(setPageValue({id: "computedFieldFieldId", value: null}));
        dispatch(setPageValue({id: "computedFieldFunctionId", value: 0}));
        dispatch(setPageValue({id: "computedFieldParentFieldId", value: null}));
        dispatch(setPageValue({id: "computedFieldMode", value: 0}));
    };

    const handleOnValidateComputedFieldDialog = () => {
        let errors = {};

        let functionId = pageState.computedFieldFunctionId;
        let parentFieldId = pageState.computedFieldParentFieldId;

        if (!functionId || functionId <= 0)
           errors = Object.assign(errors, {ComputedFunctionId: t("datasources.function")});

        if (parentFieldId == null || parentFieldId == 0)
           errors = Object.assign(errors, {ComputedParentFieldId: t("datasources.parentfield")});

        if (isEmptyObject(errors))
           return undefined;
        
        return errors;       
    };

    const handleOnCloseComputedFieldDialog = () => dispatch(closeComputedFieldDialog());


    const handleOnOpenExpressionFieldDialog = (mode, fieldId, dataTypeId, isDecimal, isPercent, body) => 
    {
        dispatch(openExpressionFieldDialog({ mode: mode, fieldId: fieldId, dataTypeId: dataTypeId, isDecimal: isDecimal, isPercent: isPercent, body: body }));
    };

    const handleOnSaveExpressionFieldDialog = (formRenderProps, t) => {
        if (handleOnValidateExpressionFieldDialog() !== undefined)                
            return;

        let field = pageState.ExpressionFieldMode == 1 ? null : pageState.formValues.Fields.find(e => e.Id == pageState.expressionFieldFieldId);

        //insert into fields
        if (field == null)
        {
            let entry = getFieldMinIdAndMaxOrder();

            let newField = {
                Id: entry.id, 
                Name: pageState.expressionFieldBody, 
                Title: pageState.expressionFieldBody, 
                IsComputedField: true, 
                FunctionId: 0, 
                FieldId: null,
                IsGrouped: false,
                ParentFieldId: null,
                ExpressionBody: pageState.expressionFieldBody,
                DataTypeId: pageState.expressionFieldDataTypeId,
                IsDecimal: pageState.expressionFieldIsDecimal,
                IsPercent: pageState.expressionFieldIsPercent,
                HasAggregateFunction: false, HasComputedFunction: false, HasExpressionBody: true,
                FieldIsAggregate: false,//field.FieldIsAggregate
                FieldIsExpression: false,//field.FieldIsAggregate
                OrderId: entry.orderId
            };

            let newFields = [...pageState.formValues.Fields, newField];
            
            dispatch(setFormValue({id: "Fields", value: newFields}));
            formRenderProps.onChange("Fields", { value: newFields});  
        }
        else
        {
            let newFields = pageState.formValues.Fields.map(e => {
                if (e.Id == field.Id)
                    return {...e, 
                        ExpressionBody: pageState.expressionFieldBody,
                        DataTypeId: pageState.expressionFieldDataTypeId,
                        IsDecimal: pageState.expressionFieldIsDecimal,
                        IsPercent: pageState.expressionFieldIsPercent
                    };
                else
                    return e;
            });

            dispatch(setFormValue({id: "Fields", value: newFields}));
            formRenderProps.onChange("Fields", { value: newFields});  
        }

        dispatch(setPageValue({id: "expressionFieldDialogOpen", value: false}));
        dispatch(setPageValue({id: "expressionFieldFieldId", value: null}));
        dispatch(setPageValue({id: "expressionFieldDataTypeId", value: 0}));
        dispatch(setPageValue({id: "expressionFieldIsDecimal", value: false}));
        dispatch(setPageValue({id: "expressionFieldIsPercent", value: false}));
        dispatch(setPageValue({id: "expressionFieldBody", value: ""}));
        dispatch(setPageValue({id: "expressionFieldMode", value: 0}));
    };

    const handleOnValidateExpressionFieldDialog = () => {
        let errors = {};

        let dataTypeId = pageState.expressionFieldDataTypeId;
        let isDecimal = pageState.expressionFieldIsDecimal;
        let isPercent = pageState.expressionFieldIsPercent;
        let body = pageState.expressionFieldBody;

        if (!dataTypeId || dataTypeId <= 0 || dataTypeId > 5)
           errors = Object.assign(errors, {DataTypeId: t("datasources.datatype")});
        else if (dataTypeId != 2 && (isDecimal == true || isPercent == true))
           errors = Object.assign(errors, {DataTypeId: t("datasources.datatype2")});
           
        if (!body || body.trim() == "")
           errors = Object.assign(errors, {Body: t("datasources.expressionbody")});
        else
        {
            let res = getExpressionBodyTokens(body);
            if (!res.status)
            {
                errors = Object.assign(errors, {Body: t("datasources.expressionbody2")});
                return errors;
            }
            else
            {
                if (res.tokens?.length > 0)
                {
                    let fields = pageState.formValues.Fields;

                    for (let tk in res.tokens)
                    {
                        let token = res.tokens[tk].trim().toLowerCase();
                        let f = fields.find(e => e.Title.trim().toLowerCase() == token);

                        if (!f)
                        {
                            errors = Object.assign(errors, {Body: t("datasources.expression1") + res.tokens[tk] + t("datasources.expression2")});
                            return errors;
                        }
                        else if (f.Id == pageState.expressionFieldFieldId)
                        {
                            errors = Object.assign(errors, {Body: t("datasources.expression3")});
                            return errors;
                        }                        
                    }
                }
            }
        }


        if (isEmptyObject(errors))
           return undefined;
        

        return errors;       
    };

    const handleOnCloseExpressionFieldDialog = () => dispatch(closeExpressionFieldDialog());

    /*TableValueList*/

    //tools visible/hidden
    const handleOnToolsToggle = (e) => {
    const isVisible = pageState.toolsVisible;

    if (isVisible)
        //hide
        dispatch(
        setPanes( [                    
            {
                size:"0%",
                min: "%",
                max: "%",
                collapsible: false
            },           
            {
                min: "84%",
                max: "78%",
                collapsible: false,
            },
            {
                size:"18%",
                min: "16%",
                max: "22%",
                collapsible: false,
            }
            ])
        );
    else
        dispatch(
        setPanes( [                    
            {
                size:"20%",
                min: "18%",
                max: "25%",
                collapsible: false
            },           
            {
                min: "53%",
                max: "66%",
                collapsible: false,
            },
            {
                size:"18%",
                min: "16%",
                max: "22%",
                collapsible: false,
            }
            ])
        );

    dispatch(setPageValue({ id: "toolsVisible", value: !isVisible }));
    //dispatch(redrawTiles());
    };

    
    return (
        <Fragment>
            <Page
                id="dataSourcesPage"
                className="datasources-page"
                title={t("datasources.datasources")}
                animation={props.animation}
            >
                <DataSourcesPageBody 
                    onSplitterChange={handleOnSplitterChange} onToolsToggle={handleOnToolsToggle}
                    dataSetsPanelRef={dataSetsPanelRef} onDatasetChange={handleOnDatasetChange} 
                    onNew={handleOnNew} onSave={handleOnSave} onClone={handleOnClone} onDelete={handleOnDelete}
                    onErrorbarClose={handleOnErrorbarClose}
                    onEntitySelected={handleOnEntitySelected} onEntityDoubleClick={handleOnEntitySelected}
                    onFieldRemove={handleOnFieldRemove} 
                    onOpenFieldsDialog={handleOnOpenFieldsDialog} onCloseFieldsDialog={handleOnCloseFieldsDialog}
                    onFieldCheck={handleOnFieldCheck} onSaveFieldsDialog={handleOnSaveFieldsDialog}
                    onFieldDrop={handleOnFieldDrop} onFieldsClear={handleOnFieldsClear} onFieldTitleChange={handleOnFieldTitleChange}
                    onFieldGroupingChange={handleonFieldGroupingChange}
                    onOpenComputedFieldDialog={handleOnOpenComputedFieldDialog} onCloseComputedFieldDialog={handleOnCloseComputedFieldDialog}
                    onSaveComputedFieldDialog={handleOnSaveComputedFieldDialog} onValidateComputedFieldDialog={handleOnValidateComputedFieldDialog}
                    onOpenExpressionFieldDialog={handleOnOpenExpressionFieldDialog} onCloseExpressionFieldDialog={handleOnCloseExpressionFieldDialog}
                    onSaveExpressionFieldDialog={handleOnSaveExpressionFieldDialog} onValidateExpressionFieldDialog={handleOnValidateExpressionFieldDialog}
                    onFieldSortDirectionChange={handleOnFieldSortDirectionChange}
                    updateDirtyState={updateDirtyState}
                />
            </Page>
            <style>
                {
                    `
                    /*page class*/
                    .datasources-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding};
                    }

                    .datasources-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .datasources-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                    /*are you sure to delete*/
                    .datasources-page-are-you-sure .k-widget33
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }    
                    
                    .datasources-page-are-you-sure .k-widget .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }
                    `
                }
            </style>
        </Fragment>
    );
};

//export const DataSourcesPage = {Component: DataSources};
